import Section from "@/components/settings/Section";
import { useAPI } from "@/contexts/APIContext";
import { useSettings } from "@/contexts/SettingsContext";
import handleError from "@/utils/handleError";
import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";

// Function to create a file from a Blob
const createFileFromBlob = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { type: blob.type });
};

// Function to get crop area pixels
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<{ file: File; url: string }> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  // Set canvas size to 512x512
  canvas.width = 512;
  canvas.height = 512;

  // Draw the cropped image on the canvas
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, 512, 512);

  // As a blob with compression
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Canvas is empty"));

        const file = createFileFromBlob(blob, "cropped-image.jpeg");
        const url = URL.createObjectURL(blob);

        resolve({ file, url });
      },
      "image/jpeg",
      0.8
    );
  });
};

interface ProfileSectionProps {
  profile: any;
}

export default function ProfileSection({ profile }: ProfileSectionProps) {
  const { api, updateUser } = useAPI();
  const { setSuccessMessage, setErrorMessage } = useSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [description, setDescription] = useState("");
  const [originalDescription, setOriginalDescription] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  // Cropping related states
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  // Initialize profile data
  useEffect(() => {
    if (!profile) return;

    const userName = profile.name || "";
    setName(userName);
    setOriginalName(userName);

    const userDescription = profile.description || "";
    setDescription(userDescription);
    setOriginalDescription(userDescription);

    setImagePreview(profile.profileImageUrl || null);
  }, [profile]);

  // Check for changes
  useEffect(() => {
    const nameChanged = name !== originalName;
    const descriptionChanged = description !== originalDescription;
    const imageChanged = !!profileImage;
    const changed = nameChanged || descriptionChanged || imageChanged;

    setIsChanged(changed);
  }, [name, description, profileImage, originalName, originalDescription]);

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleCropSave = async () => {
    if (imagePreview && croppedAreaPixels) {
      try {
        const { file, url } = await getCroppedImg(imagePreview, croppedAreaPixels);
        setProfileImage(file);
        setImagePreview(url);
        setIsCropping(false);
      } catch (error) {
        console.error("Error cropping image:", error);
      }
    }
  };

  const handleCropCancel = () => {
    setImagePreview(profile.profileImageUrl || null);
    setIsCropping(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Update user profile
      await api.user.update({
        name,
        description,
      });

      // Update profile image if changed
      if (profileImage) {
        await api.user.updateProfileImage({ file: profileImage });
      }

      setOriginalName(name);
      setOriginalDescription(description);
      setProfileImage(null);

      // Update both user data and profile data
      await updateUser(true);

      setSuccessMessage("Profile information updated successfully");
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to update profile information");
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Section
      title="Profile Information"
      onSave={handleSave}
      isChanged={isChanged}
      isSaving={isSaving}
      id="profile"
      disabled={isCropping}
    >
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>

        {isCropping ? (
          <div>
            <div className="relative h-60 w-full">
              <Cropper
                image={imagePreview || ""}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className="mt-3 flex justify-between">
              <button
                type="button"
                onClick={handleCropCancel}
                className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropSave}
                className="bg-blue-600 py-2 px-3 border border-transparent rounded-md shadow-sm text-sm leading-4 font-medium text-white hover:bg-blue-700"
              >
                Save Crop
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24">
              <img
                src={imagePreview || ""}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
              />
            </div>
            <div>
              <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageChange} className="hidden" />
              <button
                type="button"
                onClick={triggerFileInput}
                className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50"
              >
                {imagePreview ? "Change picture" : "Upload picture"}
              </button>
              {imagePreview && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="ml-2 text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-md"
          required
        />
      </div>

      <div className="mb-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          About Me
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full p-3 border border-gray-300 rounded-md"
        />
      </div>
    </Section>
  );
}
